import React, { useState, useCallback } from 'react';
import { getApiUrl } from '../../config/api';

interface BacktestTick {
  ts: string;
  score_raw: number;
  score_smoothed: number;
  completeness: number;
}

interface BacktestEvent {
  ts: string;
  type: string;
  score: number;
}

interface BacktestResult {
  date: string;
  asset: string;
  ticks: BacktestTick[];
  prices: { ts: string; price: number }[];
  events: BacktestEvent[];
  performance: {
    total_trades: number;
    total_return_pct: number;
  };
  tick_count: number;
  summary?: {
    narrative: string;
    highlights: { label: string; value: string }[];
  };
}

const PressureBacktest: React.FC = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [result, setResult] = useState<BacktestResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runBacktest = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        getApiUrl(`/api/pressure/score/TON/backtest?date=${date}`),
        { credentials: 'include' },
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data.tick_count === 0) {
          setError(`No data for ${date}. Score data started collecting recently.`);
          setResult(null);
        } else {
          setResult(data);
        }
      } else {
        setError('Failed to load backtest data');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [date]);

  const scoreMin = result ? Math.min(...result.ticks.map(t => t.score_smoothed)) : 0;
  const scoreMax = result ? Math.max(...result.ticks.map(t => t.score_smoothed)) : 0;
  const priceMin = result?.prices.length ? Math.min(...result.prices.map(p => p.price)) : 0;
  const priceMax = result?.prices.length ? Math.max(...result.prices.map(p => p.price)) : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-viz-text-primary">Backtest</h1>
        <p className="text-viz-text-secondary mt-1">
          Replay TON Pressure Score for any historical date
        </p>
      </div>

      {/* Date picker */}
      <div className="glass-card p-6 mb-6">
        <div className="flex items-end gap-4">
          <div>
            <label className="text-xs text-viz-text-secondary block mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="bg-viz-bg-tertiary text-viz-text-primary border border-viz-border rounded px-3 py-2 text-sm"
            />
          </div>
          <button
            onClick={runBacktest}
            disabled={loading}
            className="bg-viz-primary text-white rounded px-6 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Run Backtest'}
          </button>
        </div>
      </div>

      {error && (
        <div className="glass-card p-4 mb-6 border border-yellow-500/30 text-yellow-400 text-sm">
          {error}
        </div>
      )}

      {result && (
        <>
          {/* AI Summary */}
          {result.summary && (
            <div className="glass-card p-5 mb-6 border-l-4 border-l-viz-primary">
              <div className="text-sm text-viz-text-primary leading-relaxed"
                   dangerouslySetInnerHTML={{
                     __html: result.summary.narrative.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                   }}
              />
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="glass-card p-4 text-center">
              <div className="text-xs text-viz-text-secondary">Ticks</div>
              <div className="text-lg font-mono text-viz-text-primary">{result.tick_count}</div>
            </div>
            <div className="glass-card p-4 text-center">
              <div className="text-xs text-viz-text-secondary">Score Range</div>
              <div className="text-lg font-mono text-viz-text-primary">
                {scoreMin.toFixed(1)} / {scoreMax.toFixed(1)}
              </div>
            </div>
            <div className="glass-card p-4 text-center">
              <div className="text-xs text-viz-text-secondary">Regime Changes</div>
              <div className="text-lg font-mono text-viz-text-primary">{result.events.length}</div>
            </div>
            <div className="glass-card p-4 text-center">
              <div className="text-xs text-viz-text-secondary">Signal Return</div>
              <div className={`text-lg font-mono ${result.performance.total_return_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.performance.total_return_pct >= 0 ? '+' : ''}{result.performance.total_return_pct.toFixed(2)}%
              </div>
            </div>
          </div>

          {/* Score timeline (simple text-based for now, chart later) */}
          <div className="glass-card p-6 mb-6">
            <h2 className="text-lg font-semibold text-viz-text-primary mb-4">
              Score Timeline — {result.date}
            </h2>
            <div className="h-48 overflow-hidden relative">
              {/* Simple SVG sparkline */}
              <svg width="100%" height="100%" viewBox={`0 0 ${result.ticks.length} 200`} preserveAspectRatio="none">
                {/* Zero line */}
                <line x1="0" y1="100" x2={result.ticks.length} y2="100" stroke="#666" strokeWidth="0.5" strokeDasharray="4" />
                {/* Score line */}
                <polyline
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="1.5"
                  points={result.ticks.map((t, i) => {
                    const y = 100 - (t.score_smoothed / Math.max(Math.abs(scoreMin), Math.abs(scoreMax), 1)) * 90;
                    return `${i},${y}`;
                  }).join(' ')}
                />
                {/* Events */}
                {result.events.map((ev, i) => {
                  const tickIdx = result.ticks.findIndex(t => t.ts >= ev.ts);
                  if (tickIdx < 0) return null;
                  return (
                    <line key={i} x1={tickIdx} y1="0" x2={tickIdx} y2="200"
                      stroke={ev.type.includes('bullish') ? '#4ade80' : '#f87171'}
                      strokeWidth="1" strokeDasharray="2" />
                  );
                })}
              </svg>
            </div>
            <div className="flex justify-between text-xs text-viz-text-secondary mt-2">
              <span>{result.ticks[0]?.ts.split('T')[1]?.substring(0, 5) || ''}</span>
              <span>{result.ticks[result.ticks.length - 1]?.ts.split('T')[1]?.substring(0, 5) || ''}</span>
            </div>
          </div>

          {/* Events */}
          {result.events.length > 0 && (
            <div className="glass-card p-6 mb-6">
              <h2 className="text-lg font-semibold text-viz-text-primary mb-4">Regime Changes</h2>
              <div className="space-y-2">
                {result.events.map((ev, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <span className={`w-2 h-2 rounded-full ${ev.type.includes('bullish') ? 'bg-green-400' : 'bg-red-400'}`} />
                    <span className="text-viz-text-secondary font-mono">
                      {ev.ts.split('T')[1]?.substring(0, 8)}
                    </span>
                    <span className="text-viz-text-primary">
                      {ev.type.includes('bullish') ? 'Bullish crossover' : 'Bearish crossover'}
                    </span>
                    <span className="font-mono text-viz-text-secondary">
                      score: {ev.score.toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Price range */}
          {result.prices.length > 0 && (
            <div className="glass-card p-6">
              <h2 className="text-lg font-semibold text-viz-text-primary mb-2">Price Range</h2>
              <div className="text-sm text-viz-text-secondary">
                Low: ${priceMin.toFixed(4)} · High: ${priceMax.toFixed(4)} · Ticks: {result.prices.length}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PressureBacktest;
